import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { Box, Grid, Typography, Skeleton, Alert } from '@mui/material'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

const CARD_CONFIG = [
  {
    label: 'Total Salvage',
    color: '#603CBA',
    key: 'salvage',
  },
  {
    label: 'BMS Salvage',
    color: '#da532b',
    key: 'bms',
  },
  {
    label: 'Shore Cars',
    color: '#2d89ef',
    key: 'shore',
  },
  {
    label: 'Scrap',
    color: '#36a300',
    key: 'scrap',
  },
  {
    label: 'Total Booked',
    color: '#eb3c00',
    key: 'total',
  },
]

const entrypoint = process.env.REACT_APP_API_PLATFORM_ENDPOINT

function DailyReportingCard({ label, color, stats }) {
  return (
    <Grid item xs={12} sm={5} md={2}>
      <Box
        sx={{
          backgroundColor: color,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: 'initial', fontWeight: 'bold', mt: 0 }}
        >
          {label}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>Daily Total</Box>
          <Box>{stats?.daily?.total ?? 0}</Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>Monthly Total</Box>
          <Box>{stats?.monthly?.total ?? 0}</Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>Avg. Day</Box>
          <Box>{stats?.monthly?.average ?? 0}</Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>Month Forecast</Box>
          <Box>{stats?.monthly?.forecast ?? 0}</Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>Forecast vs Last Month</Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>{stats?.monthly?.diff ?? 0}%</Box>

            {stats?.monthly?.changeUp ? (
              <ArrowDropUpIcon />
            ) : (
              <ArrowDropDownIcon />
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  )
}

DailyReportingCard.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  stats: PropTypes.object,
}

export default function DailyReporting() {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const userId = localStorage.getItem('user_id')
  const isAdmin =
    localStorage.getItem('permissions')?.includes('ROLE_ADMIN') ||
    userId === '1247'

  const getDailyReport = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(
        `${entrypoint}/services/daily-reporting/get-daily-reporting`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          method: 'GET',
        }
      )
      const responseJSON = await response.json()
      if (responseJSON.Error !== undefined) {
        throw new Error(responseJSON.Error)
      }

      setData(responseJSON.data)
    } catch (err) {
      setError(err.message || 'Failed to fetch report')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Only run if the user is an admin
    if (!isAdmin) return

    getDailyReport()

    const intervalId = setInterval(() => {
      getDailyReport()
    }, 30000)

    return () => clearInterval(intervalId)
    // eslint-disable-next-line
  }, [isAdmin])

  if (!isAdmin) return null

  if (isLoading) {
    return (
      <Grid
        container
        columnSpacing={2}
        rowSpacing={2}
        columns={10}
        sx={{ mb: 2 }}
      >
        {CARD_CONFIG.map((card) => (
          <Grid item xs={12} sm={5} md={2} key={card.key}>
            <Box
              sx={{
                backgroundColor: card.color,
                padding: '16px',
                color: '#fff',
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontSize: 'initial', fontWeight: 'bold', mt: 0 }}
              >
                {card.label}
              </Typography>
              {[...Array(CARD_CONFIG.length)].map((_, i) => (
                <Skeleton key={i} width={'100%'} height={28} />
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={2}
        columns={10}
        sx={{ mb: 2 }}
      >
        {CARD_CONFIG.map((card) => (
          <DailyReportingCard
            key={card.key}
            label={card.label}
            color={card.color}
            stats={data?.[card.key]}
          />
        ))}
      </Grid>
    </>
  )
}
